import { FC } from 'react';

import { Box, Text, VStack } from '@bonusly/design-system';

import { RichTextDisplay } from '../../components/rich-text-editor/components/rich-text-display';
import { sanitize } from '../../hooks/use-sanitize-rich-text';
import { EmployeeSpotlightSection } from '../../schemas/spotlight';

interface SpotlightCardProps {
	section: EmployeeSpotlightSection;
	isActive: boolean;
}

const badgeColors = { bg: 'success.bg', color: 'success.text', borderColor: 'border.soft' };

const formatTypeAsBadge = (type: string): string => {
	return type
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

const processQuoteParagraphs = (html: string): string => {
	// Convert <p>> "..."</p> patterns to <blockquote>...</blockquote>
	return html.replace(/<p>\s*>\s*"([^"]+)"([^<]*)<\/p>/g, (match, quoteText, afterQuote) => {
		const cleanText = quoteText.trim();
		const afterText = afterQuote.trim();
		const fullText = afterText ? `${cleanText}${afterText}` : cleanText;
		return `<blockquote>${fullText}</blockquote>`;
	});
};

export const SpotlightCard: FC<SpotlightCardProps> = ({ section, isActive }) => {
	if (!isActive) {
		return null;
	}
	const processedHtml = processQuoteParagraphs(section.textHtml);
	const sanitizedHtml = sanitize(processedHtml);

	return (
		<Box
			position="absolute"
			top={0}
			left={0}
			right={0}
			opacity={isActive ? 1 : 0}
			visibility={isActive ? 'visible' : 'hidden'}
			pointerEvents={isActive ? 'auto' : 'none'}
			transition="opacity 0.3s ease, visibility 0.3s ease"
		>
			<VStack spacing={6} align="stretch">
				<Box textAlign="center">
					<Box
						as="span"
						display="inline-block"
						px={4}
						py={2}
						borderRadius="full"
						fontSize="xs"
						fontWeight="semibold"
						textTransform="uppercase"
						letterSpacing="wide"
						mb={4}
						bg={badgeColors.bg}
						color={badgeColors.color}
						border="1px solid"
						borderColor={badgeColors.borderColor}
						boxShadow="0 2px 8px rgba(0, 0, 0, 0.08)"
						backdropFilter="blur(10px)"
					>
						{formatTypeAsBadge(section.type)}
					</Box>
				</Box>

				<Box
					borderRadius="3xl"
					p="2px"
					bgGradient="linear(to-r, rgba(209, 250, 229, 0.6), rgba(254, 243, 199, 0.6))"
					boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
				>
					<Box bg="bg.surface" borderRadius="3xl" p={{ base: 8, md: 20 }} display="flex" flexDirection="column" justifyContent="center">
						<VStack spacing={8} align="stretch" maxH="500px" overflowY="hidden">
							<VStack spacing={3} align="stretch">
								<Text
									fontFamily="'Plus Jakarta Sans', sans-serif"
									fontSize="lg"
									fontWeight="medium"
									lineHeight="shorter"
									color="text.default"
									opacity={0.7}
									textTransform="uppercase"
									letterSpacing="wide"
								>
									{section.intro}
								</Text>
								<Text
									as="h2"
									fontFamily="'Plus Jakarta Sans', sans-serif"
									fontSize={{ base: '3xl', md: '4xl' }}
									fontWeight="bold"
									lineHeight="shorter"
									color="text.default"
									mt={0}
								>
									{section.title}
								</Text>
							</VStack>

							<Box
								fontSize={{ base: 'lg', md: 'xl' }}
								lineHeight="tall"
								color="text.default"
								sx={{
									'& blockquote': {
										position: 'relative',
										p: '16px 20px',
										m: ' 10px 0',
										borderLeft: 'none',
										borderRadius: '12px',
										bg: '#E6FCFA',
										fontSize: { base: 'lg', md: 'xl' },
										lineHeight: 'tall',
										color: 'text.default',
									},
								}}
							>
								<RichTextDisplay>{sanitizedHtml}</RichTextDisplay>
							</Box>
						</VStack>
					</Box>
				</Box>
			</VStack>
		</Box>
	);
};
